import { z } from 'zod';

export const GameType = z.enum([
  'trivia',
  'hangman',
  'chatwords',
  'feud',
  'crossword',
  'battleship',
  'hypefish',
  'hypeegg',
]);
export type GameType = z.infer<typeof GameType>;

export const GAME_LABELS: Record<GameType, string> = {
  trivia: 'Trivia',
  hangman: 'Hangman',
  chatwords: 'Chat Words',
  feud: 'Hype Feud',
  crossword: 'Crossword',
  battleship: 'Chattle Ship',
  hypefish: 'HypeFish',
  hypeegg: 'HypePets',
};

export const GAME_DESCRIPTIONS: Record<GameType, string> = {
  trivia: '!trivia answer — fastest correct earns points',
  hangman: '!guess <letter> | !solve <word>',
  chatwords: 'Pattern-match guesses against a hidden phrase',
  feud: 'Family Feud–style survey gameshow. Chat races to guess the top answers; per-viewer 3-strike lockout.',
  crossword: '!across|!down <num> <word> on a generated grid',
  battleship: '!fire <coord> — chat hunts a hidden fleet',
  hypefish:
    '!cast — idle fishing for chat. Cast queues, catches a fish/junk/treasure, gems awarded per rarity.',
  hypeegg:
    '!claim — random egg spawns 5-30 min apart, first viewer with enough gems wins the creature into their collection.',
};

/**
 * Reward placement. `participant` = the consolation tier (every player who
 * showed up but didn't podium). Used by `BaseGameSettings.gems.tiers` and
 * the `distributeGameRewards()` helper.
 */
export type RewardPlacement = 'first' | 'second' | 'third' | 'participant';

/**
 * Global per-streamer gem economy. Always on — every chat-driven game and
 * every engagement event (chat/follow/raid/time-watched) routes through it.
 * Game placement awards use `mode` + per-game `economyWeight`; engagement
 * income uses the flat per-event fields. All paths share the caps + cooldown
 * gate.
 */
export interface GemEconomyConfig {
  // Game placement reward shape.
  mode: 'fixed' | 'percentage' | 'tiered';
  fixedAmount: number;
  percentageBase: number;
  winnerPercentage: number;
  participantPercentage: number;
  tiers: Record<RewardPlacement, number>;
  // Engagement income. 0 = disabled.
  gemsPerMessage: number;
  /** Per-viewer cooldown between message-award triggers. */
  gemsPerMessageCooldownSec: number;
  gemsPerFollow: number;
  gemsPerRaid: number;
  gemsPerRaider: number;
  gemsPerMinuteWatched: number;
  // Caps. 0 disables the corresponding cap.
  viewerCapPerHour: number;
  viewerCapPerDay: number;
  streamerCapPerHour: number;
  /** Per-viewer cooldown across ALL game wins. 0 disables. */
  globalCooldownSeconds: number;
}

export const DEFAULT_GEM_ECONOMY_CONFIG: GemEconomyConfig = {
  mode: 'fixed',
  fixedAmount: 100,
  percentageBase: 1000,
  winnerPercentage: 50,
  participantPercentage: 5,
  tiers: { first: 200, second: 100, third: 50, participant: 10 },
  gemsPerMessage: 0,
  gemsPerMessageCooldownSec: 60,
  gemsPerFollow: 0,
  gemsPerRaid: 0,
  gemsPerRaider: 0,
  gemsPerMinuteWatched: 0,
  viewerCapPerHour: 0,
  viewerCapPerDay: 0,
  streamerCapPerHour: 0,
  globalCooldownSeconds: 0,
};

/**
 * Gem reward configuration. `fixed` keeps the legacy v1 behavior — one flat
 * amount for any win event. `percentage` carves a pool between the winner and
 * the participants. `tiered` reads a per-placement table.
 *
 * The `cooldownMinutes` knob enforces a per-(streamer, viewer) cap that the
 * distribute helper checks against `gem_transactions` rows tagged
 * `reason='game_reward'`.
 */
export interface GemRewardConfig {
  enabled: boolean;
  mode: 'fixed' | 'percentage' | 'tiered';
  /** Fixed mode: winner amount. Non-winners get a fraction (see helper). */
  fixedAmount: number;
  /** Percentage mode: total pool to carve. */
  percentageBase: number;
  /** Percentage mode: winner takes this % of pool. */
  winnerPercentage: number;
  /** Percentage mode: participants split this % of pool. */
  participantPercentage: number;
  /** Tiered mode: explicit per-placement amounts. */
  tiers: Record<RewardPlacement, number>;
  /** Hard cap per (streamer, viewer, game session). 0 = unlimited. */
  maxPerPlayer: number;
  /** Per (streamer, viewer) cooldown across game sessions. 0 = disabled. */
  cooldownMinutes: number;
}

export const DEFAULT_GEM_REWARD_CONFIG: GemRewardConfig = {
  enabled: true,
  mode: 'fixed',
  fixedAmount: 100,
  percentageBase: 1000,
  winnerPercentage: 50,
  participantPercentage: 5,
  tiers: { first: 200, second: 100, third: 50, participant: 10 },
  maxPerPlayer: 0,
  cooldownMinutes: 0,
};

export type GameWidgetTheme = 'default' | 'dark' | 'neon' | 'retro';
export type GameWidgetPosition = 'center' | 'top' | 'bottom' | 'left' | 'right';

export interface GameUiConfig {
  theme: GameWidgetTheme;
  /** 0.5 – 2.0 multiplier on the widget root. */
  scale: number;
  position: GameWidgetPosition;
  showTimer: boolean;
  showParticipantCount: boolean;
  /** "Win up to X gems!" preview on the widget header. */
  showGemRewards: boolean;
}

export const DEFAULT_GAME_UI_CONFIG: GameUiConfig = {
  theme: 'default',
  scale: 1,
  position: 'center',
  showTimer: true,
  showParticipantCount: true,
  showGemRewards: true,
};

export type GameSoundPack = 'default' | 'retro' | 'dramatic' | 'minimal';

export interface GameSoundConfig {
  enabled: boolean;
  /** 0 – 100. */
  volume: number;
  pack: GameSoundPack;
}

export const DEFAULT_GAME_SOUND_CONFIG: GameSoundConfig = {
  enabled: true,
  volume: 80,
  pack: 'default',
};

export interface GameAutomationConfig {
  autoStart: boolean;
  /** Cooldown between auto-started sessions. */
  cooldownMinutes: number;
  /** Hard cap of sessions per stream window. 0 = unlimited. */
  maxGamesPerStream: number;
}

export const DEFAULT_GAME_AUTOMATION_CONFIG: GameAutomationConfig = {
  autoStart: false,
  cooldownMinutes: 0,
  maxGamesPerStream: 0,
};

/**
 * The unified settings shape every game (standard chat games + premium / AI
 * games + widget-driven games like Slots) eventually reads. New blocks
 * (`gems`, `ui`, `sound`, `automation`) are additive — code that still reads
 * the flat `gemRewardPerWin` / `gemRewardPerScore` keeps working because the
 * legacy fields are kept in sync from `gems` when in `fixed` mode.
 */
export interface BaseGameSettings {
  enabled: boolean;
  /** Legacy: still authoritative for `gem_reward_per_win` column. Mirrors
   *  `gems.fixedAmount` when `gems.mode === 'fixed'`. */
  gemRewardPerWin: number;
  /** Legacy: gems per intra-round score event. Not yet expressed in the
   *  unified reward modes; kept as a separate knob. */
  gemRewardPerScore: number;
  /** Chat prefix override. NULL/empty = default '!'. */
  chatPrefix: string | null;
  minParticipants: number;
  maxSessionSeconds: number;
  /** When true, the runner re-initializes a fresh round instead of marking the
   *  game inactive when a round ends. Stop button still hard-stops. */
  autoRestart: boolean;
  /** Game-specific overrides merged into defaultConfig at game start. */
  custom: Record<string, unknown>;
  /** Phase 1 additive blocks — see DEFAULT_*. */
  gems: GemRewardConfig;
  ui: GameUiConfig;
  sound: GameSoundConfig;
  automation: GameAutomationConfig;
  /** Phase 4 — per-game weight (%) on the global gem economy. 100 = 1.0×.
   *  Only consulted when `streamer_gem_economy.enabled = true`. */
  economyWeight: number;
}

/**
 * Legacy alias for the standard-game settings shape. Kept so existing imports
 * keep compiling; new code should import `BaseGameSettings`.
 */
export type StandardGameSettings = BaseGameSettings;

export const DEFAULT_BASE_GAME_SETTINGS: BaseGameSettings = {
  enabled: true,
  gemRewardPerWin: 50,
  gemRewardPerScore: 10,
  chatPrefix: null,
  minParticipants: 0,
  maxSessionSeconds: 0,
  autoRestart: false,
  custom: {},
  gems: DEFAULT_GEM_REWARD_CONFIG,
  ui: DEFAULT_GAME_UI_CONFIG,
  sound: DEFAULT_GAME_SOUND_CONFIG,
  automation: DEFAULT_GAME_AUTOMATION_CONFIG,
  economyWeight: 100,
};

/** @deprecated use DEFAULT_BASE_GAME_SETTINGS */
export const DEFAULT_STANDARD_GAME_SETTINGS: BaseGameSettings = DEFAULT_BASE_GAME_SETTINGS;

/**
 * Per-game-type schema for the custom UI fields surfaced on the settings page.
 * Lets the dashboard render strongly-typed inputs without us building a
 * separate page per game. Each field maps to a key under `custom`.
 */
export type SettingsFieldType = 'number' | 'boolean' | 'string' | 'textarea';

export interface SettingsFieldSpec {
  key: string;
  label: string;
  type: SettingsFieldType;
  /** Number range. */
  min?: number;
  max?: number;
  /** Textarea: comma-separated array hint. */
  help?: string;
  /** Default value if unset on the row. */
  default?: number | boolean | string;
}

export const GAME_SETTINGS_FIELDS: Record<GameType, SettingsFieldSpec[]> = {
  trivia: [
    {
      key: 'questionDurationSec',
      label: 'Seconds per question',
      type: 'number',
      min: 5,
      max: 120,
      default: 20,
    },
    {
      key: 'maxQuestions',
      label: 'Questions per round',
      type: 'number',
      min: 1,
      max: 30,
      default: 5,
    },
    {
      key: 'eliminateOnWrong',
      label: 'Eliminate viewers on wrong answer',
      type: 'boolean',
      default: true,
    },
  ],
  hangman: [
    {
      key: 'maxWrong',
      label: 'Max wrong guesses',
      type: 'number',
      min: 3,
      max: 12,
      default: 6,
    },
    {
      key: 'hintAfterWrong',
      label: 'Reveal hint after N wrong',
      type: 'number',
      min: 0,
      max: 10,
      default: 3,
    },
  ],
  chatwords: [
    {
      key: 'sectionDurationSec',
      label: 'Section length (seconds)',
      type: 'number',
      min: 30,
      max: 600,
      default: 120,
    },
    {
      key: 'levelDurationSec',
      label: 'Level length (seconds)',
      type: 'number',
      min: 15,
      max: 300,
      default: 60,
    },
    {
      key: 'minWordLength',
      label: 'Min word length',
      type: 'number',
      min: 2,
      max: 8,
      default: 3,
    },
  ],
  feud: [
    {
      key: 'durationSec',
      label: 'Round duration (seconds)',
      type: 'number',
      min: 30,
      max: 600,
      default: 180,
    },
    {
      key: 'theme',
      label: 'Survey theme',
      type: 'string',
      default: '',
      help: 'Optional flavor passed to the AI survey generator. e.g. "gamer culture", "fantasy RPG players", "horror streams". Leave blank for the default streamer/chat vibe.',
    },
    {
      key: 'answerCount',
      label: 'Answers on the board',
      type: 'number',
      min: 4,
      max: 8,
      default: 6,
      help: '4–8 slots, ranked by points. 6 = classic Feud feel.',
    },
    {
      key: 'strikesEnabled',
      label: 'Per-viewer strikes (3 wrong → locked out for the round)',
      type: 'boolean',
      default: true,
    },
    {
      key: 'strikesPerViewer',
      label: 'Wrong guesses before lockout',
      type: 'number',
      min: 1,
      max: 10,
      default: 3,
    },
  ],
  crossword: [
    {
      key: 'size',
      label: 'Grid size',
      type: 'number',
      min: 6,
      max: 16,
      default: 10,
    },
    {
      key: 'durationSec',
      label: 'Round duration (seconds)',
      type: 'number',
      min: 60,
      max: 1800,
      default: 600,
    },
  ],
  battleship: [
    {
      key: 'size',
      label: 'Board size',
      type: 'number',
      min: 6,
      max: 12,
      default: 8,
    },
  ],
  hypefish: [
    {
      key: 'castCooldownSec',
      label: 'Per-viewer cast cooldown (seconds)',
      type: 'number',
      min: 0,
      max: 600,
      default: 30,
      help: '0 = no cooldown. Counted from the moment the cast resolves on the overlay.',
    },
    {
      key: 'maxQueueLength',
      label: 'Max queued casts',
      type: 'number',
      min: 1,
      max: 200,
      default: 50,
      help: 'Casts beyond this are rejected with a "queue full" reply.',
    },
    {
      key: 'castDurationMs',
      label: 'Cast animation (ms)',
      type: 'number',
      min: 200,
      max: 3000,
      default: 600,
    },
    {
      key: 'waitDurationMinMs',
      label: 'Wait/tension min (ms)',
      type: 'number',
      min: 500,
      max: 10000,
      default: 1500,
    },
    {
      key: 'waitDurationMaxMs',
      label: 'Wait/tension max (ms)',
      type: 'number',
      min: 500,
      max: 10000,
      default: 3000,
    },
    {
      key: 'biteDurationMs',
      label: 'Bite (ms)',
      type: 'number',
      min: 100,
      max: 2000,
      default: 350,
    },
    {
      key: 'revealDurationMs',
      label: 'Reveal card hold (ms)',
      type: 'number',
      min: 1000,
      max: 8000,
      default: 2800,
    },
    {
      key: 'weightCommon',
      label: 'Common pull weight',
      type: 'number',
      min: 0,
      max: 1000,
      default: 60,
    },
    {
      key: 'weightRare',
      label: 'Rare pull weight',
      type: 'number',
      min: 0,
      max: 1000,
      default: 25,
    },
    {
      key: 'weightEpic',
      label: 'Epic pull weight',
      type: 'number',
      min: 0,
      max: 1000,
      default: 10,
    },
    {
      key: 'weightLegendary',
      label: 'Legendary pull weight',
      type: 'number',
      min: 0,
      max: 1000,
      default: 4,
    },
    {
      key: 'weightMythic',
      label: 'Mythic pull weight',
      type: 'number',
      min: 0,
      max: 1000,
      default: 1,
      help: 'Pull weights are normalized — the absolute numbers only matter relative to each other.',
    },
    {
      key: 'triggerOnCheerBits',
      label: 'Auto-cast on cheer ≥ bits (0 = disabled)',
      type: 'number',
      min: 0,
      max: 100000,
      default: 0,
      help: 'Any Twitch cheer meeting this bit count auto-casts for the cheerer.',
    },
    {
      key: 'triggerOnSub',
      label: 'Auto-cast on new sub / resub',
      type: 'boolean',
      default: false,
    },
    {
      key: 'triggerOnChannelPoints',
      label: 'Auto-cast on channel-point redemption',
      type: 'boolean',
      default: false,
    },
    {
      key: 'triggerChannelPointTitle',
      label: 'Channel-point reward title filter (empty = any)',
      type: 'string',
      default: '',
      help: 'Case-insensitive substring match against the reward title. Use a unique HypeFish reward name to dedicate one button to the game.',
    },
    {
      key: 'boostLegendaryMultiplier',
      label: 'Boost: legendary weight ×',
      type: 'number',
      min: 1,
      max: 50,
      default: 3,
    },
    {
      key: 'boostMythicMultiplier',
      label: 'Boost: mythic weight ×',
      type: 'number',
      min: 1,
      max: 100,
      default: 5,
    },
    {
      key: 'weatherMode',
      label: 'Weather mode (visual only)',
      type: 'string',
      default: 'default',
      help: "default / night / storm / tropical — purely cosmetic, doesn't affect rates.",
    },
    {
      key: 'alwaysShowWidget',
      label: 'Always show the octagon (idle pond between casts)',
      type: 'boolean',
      default: false,
      help: 'Off (default) = pop-up-only, vanishes between casts. On = stays on screen with an idle pond.',
    },
  ],
  hypeegg: [
    {
      key: 'spawnIntervalMinSec',
      label: 'Spawn interval — min (seconds)',
      type: 'number',
      min: 30,
      max: 7200,
      default: 300,
    },
    {
      key: 'spawnIntervalMaxSec',
      label: 'Spawn interval — max (seconds)',
      type: 'number',
      min: 30,
      max: 7200,
      default: 1800,
    },
    {
      key: 'claimWindowSec',
      label: 'Claim window (seconds)',
      type: 'number',
      min: 5,
      max: 300,
      default: 30,
    },
    {
      key: 'claimCostCommon',
      label: 'Claim cost — Common (gems)',
      type: 'number',
      min: 0,
      max: 100000,
      default: 10,
    },
    {
      key: 'claimCostRare',
      label: 'Claim cost — Rare (gems)',
      type: 'number',
      min: 0,
      max: 100000,
      default: 25,
    },
    {
      key: 'claimCostEpic',
      label: 'Claim cost — Epic (gems)',
      type: 'number',
      min: 0,
      max: 100000,
      default: 75,
    },
    {
      key: 'claimCostLegendary',
      label: 'Claim cost — Legendary (gems)',
      type: 'number',
      min: 0,
      max: 100000,
      default: 150,
    },
    {
      key: 'claimCostMythic',
      label: 'Claim cost — Mythic (gems)',
      type: 'number',
      min: 0,
      max: 100000,
      default: 250,
    },
    {
      key: 'weightCommon',
      label: 'Spawn weight — Common',
      type: 'number',
      min: 0,
      max: 1000,
      default: 50,
    },
    {
      key: 'weightRare',
      label: 'Spawn weight — Rare',
      type: 'number',
      min: 0,
      max: 1000,
      default: 30,
    },
    {
      key: 'weightEpic',
      label: 'Spawn weight — Epic',
      type: 'number',
      min: 0,
      max: 1000,
      default: 15,
    },
    {
      key: 'weightLegendary',
      label: 'Spawn weight — Legendary',
      type: 'number',
      min: 0,
      max: 1000,
      default: 4,
    },
    {
      key: 'weightMythic',
      label: 'Spawn weight — Mythic',
      type: 'number',
      min: 0,
      max: 1000,
      default: 1,
    },
  ],
};

export interface GameStateChangeEvent {
  type: 'gameState';
  streamerId: string;
  gameType: GameType;
  state: unknown;
  config: unknown;
  isActive: boolean;
  timestamp: number;
}
